import React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import StoreOutlined from '@mui/icons-material/StoreOutlined';
import DirectionsCarOutlined from '@mui/icons-material/DirectionsCarOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import HotelOutlined from '@mui/icons-material/HotelOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import {
  isManageLocationTypeUsable,
} from '../../utils/productLicensing';
import './pickup-switching.css';

export const PickupSwitchingTabIcon = SwapHorizOutlined;

export const PICKUP_SWITCH_TYPES = [
  {
    key: 'kiosk',
    label: 'Kiosk',
    manageName: 'Kiosk',
    excluded: false,
    Icon: Inventory2Outlined,
  },
  {
    key: 'counter',
    label: 'Counter',
    manageName: 'Counter',
    excluded: false,
    Icon: StoreOutlined,
  },
  {
    key: 'curbside',
    label: 'Curbside',
    manageName: 'Curbside',
    excluded: false,
    Icon: DirectionsCarOutlined,
  },
  {
    key: 'courier',
    label: 'Courier',
    manageName: 'Courier',
    excluded: false,
    Icon: LocalShippingOutlined,
  },
  {
    key: 'mail_order',
    label: 'Mail Order',
    manageName: 'Mail Order',
    excluded: false,
    Icon: MailOutline,
  },
  {
    key: 'other',
    label: 'Other',
    manageName: 'Other',
    excluded: false,
    Icon: CategoryOutlined,
  },
  {
    key: 'bedside',
    label: 'Bedside',
    manageName: 'Bedside',
    excluded: true,
    Icon: HotelOutlined,
  },
];

const defaultAllowed = () => {
  const allowed = {};
  PICKUP_SWITCH_TYPES.forEach((from) => {
    allowed[from.key] = {};
    PICKUP_SWITCH_TYPES.forEach((to) => {
      if (from.key === to.key || from.excluded || to.excluded) {
        allowed[from.key][to.key] = false;
        return;
      }
      allowed[from.key][to.key] = true;
    });
  });
  return allowed;
};

export const defaultPickupSwitching = () => ({
  enabled: true,
  allowed: defaultAllowed(),
});

export const normalizePickupSwitching = (raw) => {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch (_err) {
      source = null;
    }
  }
  const next = defaultPickupSwitching();
  if (!source || typeof source !== 'object') {
    return next;
  }
  next.enabled = source.enabled !== false;
  PICKUP_SWITCH_TYPES.forEach((from) => {
    const row = (source.allowed && source.allowed[from.key]) || {};
    PICKUP_SWITCH_TYPES.forEach((to) => {
      if (from.key === to.key || from.excluded || to.excluded) {
        next.allowed[from.key][to.key] = false;
        return;
      }
      if (row[to.key] === false) {
        next.allowed[from.key][to.key] = false;
      } else if (row[to.key] === true) {
        next.allowed[from.key][to.key] = true;
      }
    });
  });
  return next;
};

const TypeLabel = ({ type }) => {
  const Icon = type.Icon;
  return (
    <span className="pickup-switch-type">
      {Icon ? <Icon fontSize="small" className="pickup-switch-type-icon" /> : null}
      <span>{type.label}</span>
    </span>
  );
};

const PickupSwitchingTab = ({
  t,
  value,
  onChange,
  entitlements,
  entitlementsBusy,
  readOnly,
}) => {
  const config = normalizePickupSwitching(value);
  const translate = (key, fallback) =>
    t ? t(key, { defaultValue: fallback }) : fallback;
  const isLicensed = (type) => {
    if (type.key === 'kiosk' || type.key === 'other' || type.key === 'mail_order') {
      return true;
    }
    return isManageLocationTypeUsable(type.manageName, entitlements);
  };

  const setEnabled = (enabled) => {
    if (readOnly) return;
    onChange({ ...config, enabled });
  };

  const setPair = (fromKey, toKey, allowed) => {
    if (readOnly) return;
    const nextAllowed = { ...config.allowed };
    nextAllowed[fromKey] = { ...(nextAllowed[fromKey] || {}), [toKey]: allowed };
    onChange({ ...config, allowed: nextAllowed });
  };

  return (
    <div className="pickup-switch-tab">
      {entitlementsBusy ? <LinearProgress className="pickup-switch-progress" /> : null}
      <h3>
        <SwapHorizOutlined className="pickup-switch-title-icon" />
        {translate('Location.PickupSwitching.Title', 'Pickup switching')}
      </h3>
      <p className="pickup-switch-help">
        {translate(
          'Location.PickupSwitching.Help',
          'Control whether patients can request a different pickup method from this location. Bedside stays excluded. Unlicensed types stay visible but disabled until that product is enabled.'
        )}
      </p>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!config.enabled}
            disabled={!!readOnly}
            onChange={(event) => setEnabled(event.target.checked)}
          />
        }
        label={translate(
          'Location.PickupSwitching.Enable',
          'Allow patients to switch pickup method from this location'
        )}
      />
      <div className="pickup-switch-matrix-wrap">
        <table className="pickup-switch-matrix">
          <thead>
            <tr>
              <th>{translate('Location.PickupSwitching.From', 'From')}</th>
              {PICKUP_SWITCH_TYPES.map((type) => (
                <th key={type.key}>
                  <TypeLabel type={type} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PICKUP_SWITCH_TYPES.map((from) => {
              const fromLocked = from.excluded || !isLicensed(from);
              return (
                <tr key={from.key} className={fromLocked ? 'is-disabled' : ''}>
                  <th>
                    <TypeLabel type={from} />
                  </th>
                  {PICKUP_SWITCH_TYPES.map((to) => {
                    const same = from.key === to.key;
                    const excluded = from.excluded || to.excluded;
                    const locked = excluded || !isLicensed(from) || !isLicensed(to) || !config.enabled;
                    const checked = !same && !excluded && !!config.allowed[from.key][to.key];
                    return (
                      <td key={to.key}>
                        {same ? (
                          <span className="pickup-switch-same">—</span>
                        ) : (
                          <Checkbox
                            color="primary"
                            checked={checked}
                            disabled={!!readOnly || locked}
                            onChange={(event) =>
                              setPair(from.key, to.key, event.target.checked)
                            }
                            inputProps={{
                              'aria-label': `${from.label} to ${to.label}`,
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="pickup-switch-footnote">
        {translate(
          'Location.PickupSwitching.BedsideNote',
          'Bedside is hospital-only and cannot be used in the patient switcher.'
        )}
      </p>
    </div>
  );
};

export default PickupSwitchingTab;



// WEBPACK FOOTER //
// ./src/components/box-list/PickupSwitchingTab.js